import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import API from '../../utils/api';
import { printBill, downloadBill } from '../../utils/pdf';
import toast from 'react-hot-toast';

const initialRow = {
  productId: '',
  isCustom: false,
  quantity: 1,
  rateType: 'manual',
  unitRate: 0,
  gst: 0,
  uom: 'PC',
  lineTotal: 0,
  maxQty: 0,
  productSearchText: '',
  showDropdown: false,
  returnQty: 0
};

export default function Billing() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editBillId = searchParams.get('edit');
  const [editId, setEditId] = useState(null);

  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [paymentMode, setPaymentMode] = useState('credit');
  const [note, setNote] = useState('');
  const [createdBill, setCreatedBill] = useState(null);
  const [rows, setRows] = useState([{ ...initialRow }]);
  const [loading, setLoading] = useState(true);

  // Keyboard navigation states for autocomplete dropdown
  const [focusedRow, setFocusedRow] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Top search bar states
  const [topSearchText, setTopSearchText] = useState('');
  const [showTopDropdown, setShowTopDropdown] = useState(false);

  // Custom paid amount during billing
  const [amountPaidState, setAmountPaidState] = useState('0');
  const [isPaidEdited, setIsPaidEdited] = useState(true);

  // Customer selection search states
  const [custSearchText, setCustSearchText] = useState('');
  const [showCustDropdown, setShowCustDropdown] = useState(false);
  const [custHighlightedIndex, setCustHighlightedIndex] = useState(0);

  // New customer inline modal state
  const [customerModal, setCustomerModal] = useState(false);
  const [newCust, setNewCust] = useState({ name: '', phone: '', street: '', city: '', pincode: '', email: '' });

  const [submittingInvoice, setSubmittingInvoice] = useState(false);
  const [submittingCustomer, setSubmittingCustomer] = useState(false);
  const [nextInvoiceNo, setNextInvoiceNo] = useState('Loading...');

  // Fetch real next invoice number from backend
  const fetchNextInvoiceNo = () => {
    if (!editBillId) {
      API.get('/bills/next-invoice-no')
        .then(r => setNextInvoiceNo(r.data.invoiceNo))
        .catch(() => setNextInvoiceNo('—'));
    }
  };

  // Load products and customers
  const fetchData = () => {
    setLoading(true);
    Promise.all([API.get('/products'), API.get('/customers')])
      .then(([p, c]) => {
        // Employees can see products but they have cost/wholesaleRate hidden.
        setProducts(p.data);
        setCustomers(c.data);
      })
      .catch(() => toast.error('Failed to load products/customers'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    fetchNextInvoiceNo();
  }, []);

  useEffect(() => {
    if (editBillId && products.length > 0) {
      setLoading(true);
      API.get(`/bills/${editBillId}`)
        .then(res => {
          const bill = res.data;
          setEditId(bill.id);
          setCustomerId(bill.customerId || '');
          setCustSearchText(bill.customer ? `${bill.customer.name} (${bill.customer.phone})` : bill.customerName || '');
          setPaymentMode(bill.paymentMode || 'cash');
          setNote(bill.note || '');
          setAmountPaidState(String(bill.amountPaid || 0));
          setIsPaidEdited(true);

          // Map items to rows
          const mappedRows = (bill.items || []).map(item => {
            const catalogProd = products.find(p => p.id === item.productId);
            const maxQty = catalogProd ? catalogProd.quantity + item.quantity : 999999;

            return {
              productId: item.productId || '',
              isCustom: !item.productId,
              quantity: item.quantity,
              rateType: item.rateType || 'manual',
              unitRate: item.unitRate,
              gst: item.gst,
              uom: item.uom || 'PC',
              lineTotal: item.lineTotal,
              maxQty: maxQty,
              productSearchText: item.productName,
              showDropdown: false,
              returnQty: item.returnQty || item.return_qty || item.returnqty || 0
            };
          });
          setRows(mappedRows.length > 0 ? mappedRows : [{ ...initialRow }]);
        })
        .catch(err => {
          console.error(err);
          toast.error('Failed to load invoice for editing.');
        })
        .finally(() => setLoading(false));
    }
  }, [editBillId, products]);

  // Invoice number is fetched from backend (see nextInvoiceNo state)

  // Add a new row to the table
  const addRow = () => {
    setRows([...rows, { ...initialRow }]);
  };

  const selectTopProduct = (p) => {
    // Check if the product is already in the bill (restore auto-increment)
    const existingIdx = rows.findIndex(r => r.productId === p.id);
    if (existingIdx !== -1) {
      const updated = [...rows];
      const qty = updated[existingIdx].quantity + 1;
      if (qty > p.quantity) {
        toast.error(`Only ${p.quantity} items available in stock!`);
        return;
      }
      updated[existingIdx].quantity = qty;
      updated[existingIdx].lineTotal = parseFloat(((qty - (updated[existingIdx].returnQty || 0)) * updated[existingIdx].unitRate).toFixed(2));
      setRows(updated);
      setTopSearchText('');
      setShowTopDropdown(false);
      toast.success(`Incremented quantity of ${p.name}`);
      return;
    }

    const updated = [...rows];
    const lastRowIndex = updated.length - 1;
    const isLastRowEmpty = lastRowIndex >= 0 && !updated[lastRowIndex].productId && !updated[lastRowIndex].isCustom && !updated[lastRowIndex].productSearchText;
    
    const newRow = {
      productId: p.id,
      isCustom: false,
      productSearchText: p.name,
      gst: p.gst || 0,
      uom: p.uom || 'PC',
      maxQty: p.quantity || 0,
      quantity: 1,
      rateType: 'retail',
      unitRate: p.retailRate || p.mrp || 0,
      lineTotal: p.retailRate || p.mrp || 0,
      showDropdown: false,
      returnQty: 0
    };
    
    if (isLastRowEmpty) {
      updated[lastRowIndex] = newRow;
    } else {
      updated.push(newRow);
    }
    
    setRows(updated);
    setTopSearchText('');
    setShowTopDropdown(false);
    toast.success(`Added ${p.name} to bill`);
  };

  const selectCustomProduct = (name) => {
    if (!name || !name.trim()) return;
    const updated = [...rows];
    const lastRowIndex = updated.length - 1;
    const isLastRowEmpty = lastRowIndex >= 0 && !updated[lastRowIndex].productId && !updated[lastRowIndex].isCustom && !updated[lastRowIndex].productSearchText;
    const newRow = {
      productId: null,
      isCustom: true,
      productSearchText: name.trim(),
      gst: 0,
      uom: 'PC',
      maxQty: 99999,
      quantity: 1,
      rateType: 'manual',
      unitRate: 0,
      lineTotal: 0,
      showDropdown: false,
      returnQty: 0
    };
    if (isLastRowEmpty) {
      updated[lastRowIndex] = newRow;
    } else {
      updated.push(newRow);
    }
    setRows(updated);
    setTopSearchText('');
    setShowTopDropdown(false);
    toast.success(`Custom product "${name.trim()}" added — fill in the rate & details`);
  };

  const handleTopSearchKeyDown = (e, filteredTopProds) => {
    // total items including the custom-add option at the bottom (if search text exists)
    const hasCustomOption = topSearchText.trim().length > 0;
    const totalItems = filteredTopProds.length + (hasCustomOption ? 1 : 0);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(totalItems - 1, prev + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(0, prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (hasCustomOption && highlightedIndex === filteredTopProds.length) {
        // The custom "Add" row is highlighted
        selectCustomProduct(topSearchText);
      } else if (filteredTopProds.length > 0 && highlightedIndex >= 0 && highlightedIndex < filteredTopProds.length) {
        const selected = filteredTopProds[highlightedIndex];
        selectTopProduct(selected);
      } else if (hasCustomOption && filteredTopProds.length === 0) {
        // No products match at all — Enter adds as custom
        selectCustomProduct(topSearchText);
      }
    } else if (e.key === 'Escape') {
      setShowTopDropdown(false);
    }
  };

  const selectCustomer = (c) => {
    setCustomerId(c.id);
    setCustSearchText(`${c.name} (${c.phone})`);
    setShowCustDropdown(false);
  };

  const handleCustBlur = () => {
    setTimeout(() => {
      setShowCustDropdown(false);
      const selected = customers.find(cust => String(cust.id) === String(customerId));
      if (selected) {
        setCustSearchText(`${selected.name} (${selected.phone})`);
      } else {
        setCustSearchText('');
      }
    }, 250);
  };

  const handleCustSearchKeyDown = (e, displayedCustomers) => {
    const totalItems = displayedCustomers.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCustHighlightedIndex(prev => Math.min(totalItems - 1, prev + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCustHighlightedIndex(prev => Math.max(0, prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (displayedCustomers.length > 0 && custHighlightedIndex >= 0 && custHighlightedIndex < displayedCustomers.length) {
        const selected = displayedCustomers[custHighlightedIndex];
        selectCustomer(selected);
      }
    } else if (e.key === 'Escape') {
      setShowCustDropdown(false);
    }
  };

  // Remove a row
  const removeRow = (index) => {
    if (rows.length === 1) {
      setRows([{ ...initialRow }]);
    } else {
      setRows(rows.filter((_, idx) => idx !== index));
    }
  };

  // Handle changes in table cell inputs
  const handleCellChange = (index, field, value) => {
    const updated = [...rows];
    const row = { ...updated[index] };

    if (field === 'selectProduct') {
      const prod = value;
      row.productId = prod.id;
      row.productSearchText = prod.name;
      row.gst = prod.gst || 0;
      row.uom = prod.uom || 'PC';
      row.maxQty = prod.quantity || 0;
      row.showDropdown = false;
      
      // Auto-select rate value based on rateType
      if (row.rateType === 'retail') {
        row.unitRate = prod.retailRate || prod.mrp || 0;
      } else if (row.rateType === 'wholesale') {
        row.unitRate = (prod.wholesaleRate && !isNaN(prod.wholesaleRate) && parseFloat(prod.wholesaleRate) > 0) ? prod.wholesaleRate : (prod.retailRate || prod.mrp || 0);
      }
      
      if (row.quantity > row.maxQty) {
        row.quantity = row.maxQty;
        toast.error(`Only ${row.maxQty} in stock for ${prod.name}`);
      }
    } else if (field === 'productSearchText') {
      row.productSearchText = value;
      // If they clear the text, we deselect the product
      if (value === '') {
        row.productId = '';
        row.unitRate = 0;
        row.gst = 0;
        row.maxQty = 0;
      }
    } else if (field === 'showDropdown') {
      row.showDropdown = value;
    } else if (field === 'gst') {
      // GST dropdown for custom products
      row.gst = parseInt(value) || 0;
    } else if (field === 'quantity') {
      const val = parseInt(value) || 1;
      row.quantity = Math.max(1, val);
      if (row.returnQty > row.quantity) {
        row.returnQty = row.quantity;
      }
    } else if (field === 'returnQty') {
      const val = parseInt(value) || 0;
      row.returnQty = Math.max(0, Math.min(row.quantity, val));
    } else if (field === 'rateType') {
      row.rateType = value;
      const prod = products.find(p => p.id === row.productId);
      if (prod) {
        if (value === 'retail') {
          row.unitRate = prod.retailRate || prod.mrp || 0;
        } else if (value === 'wholesale') {
          row.unitRate = (prod.wholesaleRate && !isNaN(prod.wholesaleRate) && parseFloat(prod.wholesaleRate) > 0) ? prod.wholesaleRate : (prod.retailRate || prod.mrp || 0);
        }
      }
    } else if (field === 'unitRate') {
      row.unitRate = parseFloat(value) || 0;
    } else if (field === 'uom') {
      row.uom = value;
    }

    const netQty = row.quantity - (row.returnQty || 0);
    row.lineTotal = parseFloat((netQty * row.unitRate).toFixed(2));
    updated[index] = row;
    setRows(updated);
  };

  // Calculations (Tax Inclusive)
  const calculateTotals = () => {
    let sub = 0;
    let tax = 0;
    rows.forEach(r => {
      const rowTotal = r.lineTotal || 0;
      const gstRate = r.gst || 0;
      const untaxedLineTotal = rowTotal / (1 + gstRate / 100);
      const lineTax = rowTotal - untaxedLineTotal;
      sub += untaxedLineTotal;
      tax += lineTax;
    });
    return {
      subtotal: parseFloat(sub.toFixed(2)),
      totalTax: parseFloat(tax.toFixed(2)),
      grandTotal: parseFloat(rows.reduce((s, r) => s + r.lineTotal, 0).toFixed(2))
    };
  };

  const { subtotal, totalTax, grandTotal } = calculateTotals();

  // Save/Generate invoice
  const handleGenerateInvoice = async () => {
    if (submittingInvoice) return;
    if (!customerId) return toast.error('Please select a customer');
    
    // Filter valid rows — both catalog and custom products
    const validItems = rows.filter(r => (r.productId || r.isCustom) && r.quantity > 0);
    if (validItems.length === 0) return toast.error('Please add at least one product with quantity');

    // Check custom rows have a name and a rate
    const missingName = validItems.find(r => r.isCustom && !r.productSearchText?.trim());
    if (missingName) return toast.error('Please enter a product name for all custom entries');
    const missingRate = validItems.find(r => r.isCustom && (!r.unitRate || r.unitRate <= 0));
    if (missingRate) return toast.error('Please enter a unit rate for all custom product entries');

    // Pre-validate: make sure all catalog (non-custom) items still exist in the loaded products list.
    // If a product was deleted by admin or the page is stale, catch it here before hitting the backend.
    for (const r of validItems) {
      if (!r.isCustom && r.productId) {
        const existsInState = products.some(p => String(p.id) === String(r.productId));
        if (!existsInState) {
          return toast.error(
            `"${r.productSearchText || r.productId}" is no longer in the product list. ` +
            `Please refresh the page or add it as a custom product.`
          );
        }
      }
    }

    const actualAmountPaid = isPaidEdited ? parseFloat(amountPaidState) : grandTotal;

    setSubmittingInvoice(true);
    try {
      const payload = {
        customerId,
        // Build items: always include productName so backend has a fallback name for every item
        items: validItems.map(r => {
          const isCust = r.isCustom || !r.productId;
          return {
            productId: isCust ? null : r.productId,
            productName: (r.productSearchText || 'Custom Product').trim(),
            isCustom: isCust,
            quantity: r.quantity,
            returnQty: r.returnQty || 0,
            unitRate: r.unitRate,
            rateType: r.rateType,
            gst: r.gst,
            uom: r.uom
          };
        }),
        paymentMode,
        amountPaid: actualAmountPaid,
        note
      };

      let res;
      if (editId) {
        res = await API.put(`/bills/${editId}`, payload);
        toast.success(`Bill ${res.data.invoiceNo} updated successfully!`);
        setEditId(null);
        navigate('/billing', { replace: true });
      } else {
        res = await API.post('/bills', payload);
        toast.success(`Bill ${res.data.invoiceNo} generated successfully!`);
      }
      setCreatedBill(res.data);
      setRows([{ ...initialRow }]);
      setCustomerId('');
      setCustSearchText('');
      setNote('');
      setPaymentMode('credit');
      setAmountPaidState('0');
      setIsPaidEdited(true);
      fetchNextInvoiceNo(); // Refresh for next bill
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to generate invoice');
    } finally {
      setSubmittingInvoice(false);
    }
  };

  // Inline Quick Add Customer
  const handleAddCustomer = async () => {
    if (submittingCustomer) return;
    if (!newCust.name || !newCust.phone) return toast.error('Name and Phone are required');
    setSubmittingCustomer(true);
    try {
      const addressJson = JSON.stringify({
        street: newCust.street || '',
        city: newCust.city || '',
        pincode: newCust.pincode || ''
      });
      const customerData = {
        name: newCust.name,
        phone: newCust.phone,
        email: newCust.email,
        address: addressJson
      };
      const res = await API.post('/customers', customerData);
      toast.success('Customer registered successfully');
      setCustomers([...customers, res.data]);
      setCustomerId(res.data.id);
      setCustSearchText(`${res.data.name} (${res.data.phone})`);
      setCustomerModal(false);
      setNewCust({ name: '', phone: '', street: '', city: '', pincode: '', email: '' });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Customer creation failed');
    } finally {
      setSubmittingCustomer(false);
    }
  };

  if (createdBill) {
    return (
      <Layout title="Invoice Generated">
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div className="card fade-in">
            <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: '3.5rem', marginBottom: 16 }}>✅</div>
              <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--navy)', marginBottom: 8 }}>Invoice Generated!</h2>
              <p style={{ color: 'var(--gray-500)', marginBottom: 4 }}><strong>Invoice No: {createdBill.invoiceNo}</strong></p>
              <p style={{ color: 'var(--gray-500)', marginBottom: 24 }}>Customer: {createdBill.customer?.name}</p>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', color: 'var(--gold)', marginBottom: 28 }}>₹{createdBill.total?.toFixed(2)}</div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-primary btn-lg" onClick={() => {
                  const cust = customers.find(c => String(c.id) === String(createdBill.customerId)) || createdBill.customer;
                  printBill({ ...createdBill, customer: cust });
                }}>🖨️ Print Invoice</button>
                <button className="btn btn-outline btn-lg" onClick={() => {
                  const cust = customers.find(c => String(c.id) === String(createdBill.customerId)) || createdBill.customer;
                  downloadBill({ ...createdBill, customer: cust });
                }}>⬇️ Download PDF</button>
                <button className="btn btn-gold btn-lg" onClick={() => { setCreatedBill(null); fetchData(); }}>+ New Invoice</button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

    const filteredTopProds = products.filter(p => 
      !topSearchText || 
      p.name.toLowerCase().includes(topSearchText.toLowerCase()) ||
      (p.hsnCode && p.hsnCode.includes(topSearchText))
    );

    const filteredCustomers = customers.filter(c => {
      if (!custSearchText.trim()) return true;
      const query = custSearchText.toLowerCase();
      return (
        c.name.toLowerCase().includes(query) ||
        c.phone.includes(query) ||
        (c.email && c.email.toLowerCase().includes(query)) ||
        c.id.toLowerCase().includes(query)
      );
    });
    const displayedCustomers = filteredCustomers.slice(0, 50);

    return (
      <Layout title="Sales Entry (New Bill)">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray-400)' }}>Loading software details...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Header Customer Selection Details */}
          <div className="card">
            <div className="card-body" style={{ display: 'flex', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: 1, minWidth: 260, marginBottom: 0, position: 'relative' }}>
                <label className="form-label">CUSTOMER NAME *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      className="form-control"
                      placeholder="🔍 Search customer by name, mobile, email..."
                      value={custSearchText}
                      onFocus={() => setShowCustDropdown(true)}
                      onClick={() => setShowCustDropdown(true)}
                      onBlur={handleCustBlur}
                      onKeyDown={e => handleCustSearchKeyDown(e, displayedCustomers)}
                      onChange={e => {
                        setCustSearchText(e.target.value);
                        setCustomerId('');
                        setCustHighlightedIndex(0);
                        setShowCustDropdown(true);
                      }}
                      style={{
                        padding: '10px 14px',
                        fontSize: '0.95rem',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--white)',
                        color: 'var(--navy)',
                      }}
                    />
                    {showCustDropdown && (
                      <div
                        className="customer-autocomplete-dropdown"
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: '100%',
                          zIndex: 1000,
                          background: 'var(--white)',
                          border: '1px solid var(--gray-200)',
                          borderRadius: 'var(--radius-sm)',
                          boxShadow: 'var(--shadow-md)',
                          maxHeight: 250,
                          overflowY: 'auto',
                          marginTop: 4
                        }}
                      >
                        {displayedCustomers.length === 0 ? (
                          <div style={{ padding: '8px 12px', color: 'var(--gray-400)', fontSize: '0.85rem' }}>No matching customers found</div>
                        ) : (
                          displayedCustomers.map((c, cIndex) => (
                            <div
                              key={c.id}
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => selectCustomer(c)}
                              style={{
                                padding: '10px 14px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                borderBottom: '1px solid var(--gray-50)',
                                background: custHighlightedIndex === cIndex ? 'var(--gold-pale)' : '',
                                borderLeft: custHighlightedIndex === cIndex ? '3px solid var(--gold)' : '3px solid transparent',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.name}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 2 }}>
                                  Phone: {c.phone} {c.email ? ` | Email: ${c.email}` : ''}
                                </div>
                              </div>
                              {c.balance > 0 && (
                                <div style={{ textAlign: 'right' }}>
                                  <span style={{ fontSize: '0.75rem', color: 'var(--red)', background: '#fff0f0', padding: '2px 6px', borderRadius: 4, fontWeight: 'bold' }}>
                                    Due: ₹{c.balance.toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <button className="btn btn-gold" onClick={() => setCustomerModal(true)} style={{ height: 42 }}>+</button>
                </div>
              </div>
              <div className="form-group" style={{ width: 180, marginBottom: 0 }}>
                <label className="form-label">BILL#</label>
                <input className="form-control" value={nextInvoiceNo} disabled style={{ background: 'var(--gray-50)', fontWeight: 'bold', color: 'var(--navy)' }} />
              </div>
            </div>
          </div>

          {/* Top Product Search Bar (Google Search style) */}
          <div className="card" style={{ boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body" style={{ position: 'relative', padding: '16px 20px' }}>
              <label className="form-label" style={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 8, color: 'var(--navy)' }}>Type Product Name / HSN to Search & Add</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-control"
                  placeholder="🔍 Type product name, HSN code, or select from list..."
                  value={topSearchText}
                  onFocus={() => setShowTopDropdown(true)}
                  onClick={() => setShowTopDropdown(true)}
                  onBlur={() => {
                    setTimeout(() => setShowTopDropdown(false), 250);
                  }}
                  onKeyDown={e => handleTopSearchKeyDown(e, filteredTopProds)}
                  onChange={e => {
                    setTopSearchText(e.target.value);
                    setHighlightedIndex(0);
                    setShowTopDropdown(true);
                  }}
                  style={{
                    padding: '12px 16px',
                    fontSize: '1rem',
                    borderRadius: 'var(--radius-md)',
                    border: '1.5px solid var(--gold)',
                    background: 'var(--white)',
                    color: 'var(--navy)',
                    boxShadow: 'var(--shadow-sm)'
                  }}
                />
                {showTopDropdown && (
                  <div
                    className="product-autocomplete-dropdown"
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: '100%',
                      zIndex: 1000,
                      background: 'var(--white)',
                      border: '1px solid var(--gray-200)',
                      borderRadius: 'var(--radius-sm)',
                      boxShadow: 'var(--shadow-md)',
                      maxHeight: 255,
                      overflowY: 'auto',
                      marginTop: 4
                    }}
                  >
                    {filteredTopProds.length === 0 && !topSearchText.trim() ? (
                      <div style={{ padding: '8px 12px', color: 'var(--gray-400)', fontSize: '0.85rem' }}>Start typing to search products...</div>
                    ) : (
                      <>
                        {filteredTopProds.map((p, pIndex) => (
                          <div
                            key={p.id}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => selectTopProduct(p)}
                            style={{
                              padding: '10px 14px',
                              cursor: p.quantity > 0 ? 'pointer' : 'not-allowed',
                              fontSize: '0.85rem',
                              borderBottom: '1px solid var(--gray-50)',
                              background: highlightedIndex === pIndex ? 'var(--gold-pale)' : (p.quantity <= 0 ? '#fafafa' : ''),
                              opacity: p.quantity <= 0 ? 0.6 : 1,
                              textAlign: 'left',
                              borderLeft: highlightedIndex === pIndex ? '3px solid var(--gold)' : 'none',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{p.name}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 2 }}>
                                Stock: {p.quantity} {p.uom || 'PC'} {p.rack !== undefined && p.rack !== null && p.rack !== '' ? `| Rack: ${p.rack}` : ''}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 600, color: 'var(--green)' }}>₹{p.retailRate?.toFixed(2)}</div>
                              {p.hsnCode && <span style={{ fontSize: '0.65rem', color: 'var(--blue)', background: 'var(--blue-light)', padding: '1px 4px', borderRadius: 4 }}>HSN: {p.hsnCode}</span>}
                            </div>
                          </div>
                        ))}
                        {topSearchText.trim() && (
                          <div
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => selectCustomProduct(topSearchText)}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              borderTop: filteredTopProds.length > 0 ? '2px dashed var(--gray-200)' : 'none',
                              background: highlightedIndex === filteredTopProds.length ? 'var(--amber-light)' : 'var(--gray-50)',
                              borderLeft: highlightedIndex === filteredTopProds.length ? '3px solid var(--amber)' : '3px solid transparent',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              color: 'var(--amber)'
                            }}
                          >
                            <span style={{ fontSize: '1.1rem' }}>✏️</span>
                            <div>
                              <div style={{ fontWeight: 700, color: 'var(--amber)' }}>+ Add Custom Product</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginTop: 1 }}>"<strong>{topSearchText}</strong>" — manual entry, no stock deduction</div>
                            </div>
                          </div>
                        )}
                        {filteredTopProds.length === 0 && topSearchText.trim() && (
                          <div style={{ padding: '6px 14px 8px', fontSize: '0.78rem', color: 'var(--gray-400)', textAlign: 'center' }}>No catalog matches. Use the custom option above.</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Grid spreadsheet Table */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Sales Invoice Items Grid</span>
              <button className="btn btn-primary btn-sm" onClick={addRow}>+ Add Row</button>
            </div>
            <div className="table-wrap" style={{ padding: 4 }}>
              <table style={{ minWidth: 800 }}>
                <thead>
                  <tr>
                    <th style={{ width: 60, textAlign: 'center' }}>S.NO.</th>
                    <th>PRODUCT NAME</th>
                    <th style={{ width: 110 }}>QTY BILLED</th>
                    <th style={{ width: 130 }}>QTY RETURNED</th>
                    <th style={{ width: 160 }}>RATE TYPE</th>
                    <th style={{ width: 130 }}>UNIT RATE (₹)</th>
                    <th style={{ width: 80, textAlign: 'center' }}>GST%</th>
                    <th style={{ width: 140 }}>TOTAL (₹)</th>
                    <th style={{ width: 110 }}>UOM</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    return (
                      <tr key={index} style={{ background: row.returnQty > 0 ? 'rgba(239, 68, 68, 0.05)' : (row.isCustom ? 'rgba(217, 119, 6, 0.04)' : '') }}>
                        <td style={{ textAlign: 'center', color: 'var(--gray-500)', fontWeight: 600 }}>
                          {index + 1}
                          {row.isCustom && <div style={{ fontSize: '0.6rem', color: 'var(--amber)', fontWeight: 700, marginTop: 2 }}>CUSTOM</div>}
                        </td>
                        <td>
                          {row.isCustom ? (
                            <input
                              className="form-control"
                              type="text"
                              placeholder="Enter product name..."
                              value={row.productSearchText}
                              onChange={e => handleCellChange(index, 'productSearchText', e.target.value)}
                              style={{ border: '1.5px solid var(--amber)', background: 'transparent', fontWeight: 600, color: 'var(--navy)', fontSize: '0.875rem' }}
                            />
                          ) : (
                            <div style={{ padding: '6px 0', fontSize: '0.875rem', fontWeight: 600, color: 'var(--navy)' }}>
                              {row.productSearchText || <span style={{ color: 'var(--gray-400)', fontWeight: 'normal', fontStyle: 'italic' }}>— No product selected —</span>}
                            </div>
                          )}
                        </td>
                        <td>
                          <input 
                            className="form-control"
                            type="number"
                            min="1"
                            value={row.quantity}
                            onChange={e => handleCellChange(index, 'quantity', e.target.value)}
                            disabled={!row.productId && !row.isCustom}
                            style={{ border: 'none', background: 'transparent', textAlign: 'center', fontWeight: 'bold' }}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
                            <input 
                              className="form-control"
                              type="number"
                              min="0"
                              max={row.quantity}
                              value={row.returnQty || 0}
                              onChange={e => handleCellChange(index, 'returnQty', e.target.value)}
                              disabled={!row.productId && !row.isCustom}
                              style={{ 
                                flex: 1,
                                minWidth: '65px',
                                border: row.returnQty > 0 ? '1.5px solid var(--red)' : 'none', 
                                background: row.returnQty > 0 ? 'var(--red-light)' : 'transparent', 
                                textAlign: 'center', 
                                fontWeight: 'bold',
                                color: row.returnQty > 0 ? 'var(--red)' : ''
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleCellChange(index, 'returnQty', row.quantity)}
                              disabled={!row.productId && !row.isCustom}
                              style={{ 
                                padding: '4px 8px', 
                                fontSize: '0.7rem', 
                                background: row.returnQty === row.quantity ? 'var(--red)' : 'var(--gray-100)',
                                color: row.returnQty === row.quantity ? 'var(--white)' : 'var(--gray-600)',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                transition: 'all 0.15s ease'
                              }}
                              title="Return Full Quantity"
                            >
                              ALL
                            </button>
                          </div>
                        </td>
                        <td>
                          <select
                            className="form-control"
                            value={row.rateType}
                            onChange={e => handleCellChange(index, 'rateType', e.target.value)}
                            disabled={!row.productId && !row.isCustom}
                            style={{ border: 'none', background: 'transparent' }}
                          >
                            <option value="retail">Retail Rate</option>
                            <option value="wholesale">Wholesale Rate</option>
                            <option value="manual">Manual Entry</option>
                          </select>
                        </td>
                        <td>
                          <input 
                            className="form-control"
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.unitRate}
                            onChange={e => handleCellChange(index, 'unitRate', e.target.value)}
                            disabled={!row.isCustom && (!row.productId || row.rateType !== 'manual')}
                            style={{ border: row.isCustom ? '1.5px solid var(--amber)' : 'none', background: 'transparent', fontWeight: 'bold', color: 'var(--green)' }}
                          />
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 500 }}>
                          {row.isCustom ? (
                            <select
                              className="form-control"
                              value={String(row.gst)}
                              onChange={e => handleCellChange(index, 'gst', e.target.value)}
                              style={{ border: '1.5px solid var(--amber)', background: 'transparent', textAlign: 'center', fontSize: '0.8rem' }}
                            >
                              <option value="0">0%</option>
                              <option value="5">5%</option>
                              <option value="12">12%</option>
                              <option value="18">18%</option>
                              <option value="28">28%</option>
                            </select>
                          ) : (
                            <span>{row.gst}%</span>
                          )}
                        </td>
                        <td>
                          <input 
                            className="form-control"
                            value={row.lineTotal.toFixed(2)}
                            disabled
                            style={{ border: 'none', background: 'transparent', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--navy)', textAlign: 'right' }}
                          />
                        </td>
                        <td>
                          <select
                            className="form-control"
                            value={row.uom}
                            onChange={e => handleCellChange(index, 'uom', e.target.value)}
                            disabled={!row.productId && !row.isCustom}
                            style={{ border: 'none', background: 'transparent' }}
                          >
                            <option value="PC">PC</option>
                            <option value="MTR">MTR</option>
                            <option value="FT">FT</option>
                            <option value="KG">KG</option>
                            <option value="LTR">LTR</option>
                            <option value="BOX">BOX</option>
                            <option value="SET">SET</option>
                          </select>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button 
                            onClick={() => removeRow(index)} 
                            style={{ background: 'transparent', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: '1.1rem' }}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Subtotal Calculations & Payment Mode */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Bill Meta Options</span></div>
              <div className="card-body">
                <div className="form-row-3">
                  <div className="form-group">
                    <label className="form-label">Payment Mode</label>
                    <select className="form-control" value={paymentMode} onChange={e => {
                      const mode = e.target.value;
                      setPaymentMode(mode);
                      if (mode === 'credit' || mode === 'discount') {
                        setAmountPaidState('0');
                        setIsPaidEdited(true);
                      } else {
                        setIsPaidEdited(false);
                      }
                    }}>
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="upi">UPI (GPay/PhonePe)</option>
                      <option value="credit">Credit</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Amount Paid (₹) *
                      {paymentMode === 'discount' && (
                        <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: 'var(--amber)', background: 'var(--amber-light)', padding: '2px 7px', borderRadius: 99 }}>
                          DISCOUNT / WAIVE OFF
                        </span>
                      )}
                      {paymentMode === 'credit' && (
                        <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: 'var(--red)', background: 'var(--red-light)', padding: '2px 7px', borderRadius: 99 }}>
                          CREDIT / DUE
                        </span>
                      )}
                    </label>
                    <input 
                      className="form-control"
                      type="number"
                      min="0"
                      max={grandTotal}
                      step="0.01"
                      value={isPaidEdited ? amountPaidState : grandTotal.toFixed(2)}
                      onChange={e => {
                        setAmountPaidState(e.target.value);
                        setIsPaidEdited(true);
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Note / References</label>
                    <input className="form-control" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Paid via GPay..." />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--gray-600)' }}>
                  <span>Subtotal Amount</span>
                  <strong>₹{subtotal.toFixed(2)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--gray-600)' }}>
                  <span>Total Tax (GST Amount)</span>
                  <strong>₹{totalTax.toFixed(2)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--navy)', borderTop: '2px solid var(--gold)', paddingTop: 12, marginTop: 4 }}>
                  <span>GRAND TOTAL</span>
                  <strong>₹{grandTotal.toFixed(2)}</strong>
                </div>
                <button 
                  className="btn btn-gold btn-lg" 
                  style={{ width: '100%', justifyContent: 'center', marginTop: 12 }} 
                  onClick={handleGenerateInvoice}
                  disabled={submittingInvoice}
                >
                  {submittingInvoice ? '⏳ Processing...' : editId ? '💾 Update Sales Bill' : '🧾 Generate Sales Bill'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Quick Add Modal */}
      {customerModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCustomerModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Quick Register Customer</span>
              <button className="modal-close" onClick={() => setCustomerModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input 
                    className="form-control" 
                    value={newCust.name} 
                    onChange={e => setNewCust({ ...newCust, name: e.target.value })} 
                    placeholder="Customer Name" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone / Mobile *</label>
                  <input 
                    className="form-control" 
                    value={newCust.phone} 
                    onChange={e => setNewCust({ ...newCust, phone: e.target.value })} 
                    placeholder="Mobile number" 
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Street Name</label>
                <input 
                  className="form-control" 
                  value={newCust.street || ''} 
                  onChange={e => setNewCust({ ...newCust, street: e.target.value })} 
                  placeholder="Street name / Door No." 
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input 
                    className="form-control" 
                    value={newCust.city || ''} 
                    onChange={e => setNewCust({ ...newCust, city: e.target.value })} 
                    placeholder="City" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Pincode</label>
                  <input 
                    className="form-control" 
                    value={newCust.pincode || ''} 
                    onChange={e => setNewCust({ ...newCust, pincode: e.target.value })} 
                    placeholder="Pincode" 
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input 
                  className="form-control" 
                  type="email" 
                  value={newCust.email} 
                  onChange={e => setNewCust({ ...newCust, email: e.target.value })} 
                  placeholder="Email ID (optional)" 
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setCustomerModal(false)} disabled={submittingCustomer}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddCustomer} disabled={submittingCustomer}>
                {submittingCustomer ? '⏳ Saving...' : 'Save & Select'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
